import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import MessageBubble from './MessageBubble';
import { useToast } from './overlay/Toast';

/**
 * Claude-inspired clean chat page with file upload support.
 * Quick replies rendered above input bar (thumb-friendly).
 */
export default function ChatPage({
  messages,
  sending,
  onSend,
  onSendFile,
  onNewSession,
  onMenuOpen,
  onShowDetail,
  appName,
  token,
  unreadCount = 0,
  onBellClick,
  seasonalContext,
}) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [input, setInput] = useState('');
  const [listening, setListening] = useState(false);
  const [hasSpeech, setHasSpeech] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.lang = i18n.language || 'de';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(prev => prev ? prev + ' ' + transcript : transcript);
      setListening(false);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    setHasSpeech(true);
    return () => recognition.abort();
  }, [i18n.language]);

  const handleSend = useCallback(() => {
    if (pendingFile) {
      onSendFile(pendingFile, input.trim());
      setPendingFile(null);
      setInput('');
      document.activeElement?.blur();
      return;
    }
    if (!input.trim() || sending) return;
    onSend(input.trim());
    setInput('');
    document.activeElement?.blur();
  }, [input, sending, onSend, onSendFile, pendingFile]);

  const handleQuickReply = useCallback((text) => {
    if (sending) return;
    onSend(text);
  }, [sending, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleVoice = () => {
    if (!recognitionRef.current) return;
    if (listening) recognitionRef.current.stop();
    else { recognitionRef.current.start(); setListening(true); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({
        variant: 'error',
        title: t('upload.tooLargeTitle', { defaultValue: 'Datei zu groß' }),
        description: t('upload.tooLargeBody', { defaultValue: 'Bitte wähle eine Datei unter 10 MB.' }),
      });
      return;
    }
    setPendingFile(file);
    e.target.value = '';
  };

  const clearPendingFile = () => setPendingFile(null);

  const showWelcome = messages.length === 0;

  // Get quick replies from the last assistant message (if any)
  const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant' && !m.isError);
  const quickReplies = lastAssistant?.quick_replies || [];

  // Smart context-aware quick action chips
  // Seasonal context from server wins; fall back to month-aware defaults
  const month = new Date().getMonth() + 1; // 1-12
  const isSprayingSeason = month >= 3 && month <= 10;
  const isFertSeason = month >= 3 && month <= 5;
  const isHarvestSeason = month >= 7 && month <= 9;
  const isSowingSeason = month >= 9 && month <= 10;

  const smartWelcomeChips = (() => {
    const chips = [];
    if (isSprayingSeason) chips.push({ key: 'canISpray', msg: t('quickActions.canISpray') });
    if (isFertSeason) chips.push({ key: 'logFertilizer', msg: t('quickActions.logFertilizer') });
    if (isHarvestSeason) chips.push({ key: 'logHarvest', msg: t('quickActions.logHarvest') });
    if (isSowingSeason) chips.push({ key: 'logSpray', msg: t('quickActions.logSpray') });
    chips.push({ key: 'weeklyPlan', msg: t('quickActions.weeklyPlan') });
    chips.push({ key: 'weather', msg: t('quickActions.weatherDetailed') });
    chips.push({ key: 'nBalance', msg: t('quickActions.nBalanceDetailed') });
    return chips.slice(0, 4);
  })();

  const welcomeChips = seasonalContext?.quick_actions
    ? seasonalContext.quick_actions.map((action) => ({ label: action, value: action }))
    : smartWelcomeChips.map(c => ({ label: t(`quickActions.${c.key}`), value: c.msg }));

  // Show chips above input: either quick replies from AI or welcome chips
  const activeChips = showWelcome
    ? welcomeChips
    : quickReplies.map(r => ({ label: r, value: r }));

  return (
    <div className="h-full flex flex-col bg-[var(--aif-bg-page)]">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 flex-shrink-0">
        <button
          onClick={onMenuOpen}
          className="w-11 h-11 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
          aria-label="Menu"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
          {appName || t('app.name')}
        </span>
        <div className="flex items-center gap-1">
          {/* Notification Bell */}
          <button
            onClick={onBellClick}
            className="relative w-11 h-11 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
            aria-label="Notifications"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 bg-red-500 rounded-full text-[9px] text-white font-bold flex items-center justify-center px-0.5">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {/* New chat */}
          <button
            onClick={onNewSession}
            className="w-11 h-11 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
            aria-label="New chat"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
            </svg>
          </button>
        </div>
      </header>

      {/* Messages area */}
      <div
        className="flex-1 overflow-y-auto overscroll-contain min-h-0"
        onClick={(e) => {
          const tag = e.target.tagName;
          if (tag !== 'A' && tag !== 'BUTTON' && tag !== 'IMG' && !e.target.closest('a, button')) {
            document.activeElement?.blur();
          }
        }}
      >
        {showWelcome ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <div className="w-16 h-16 bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-2xl flex items-center justify-center mb-6">
              <span className="text-[var(--aif-color-primary)] font-bold text-2xl">A</span>
            </div>
            <h1 className="text-2xl font-semibold text-[var(--aif-text-primary)] mb-2 leading-tight">
              {seasonalContext?.greeting || t('chat.welcome')}
            </h1>
            <p className="text-base text-[var(--aif-text-muted)] max-w-sm leading-relaxed">
              {t('chat.welcomeMessage')}
            </p>
            <p className="text-xs text-[var(--aif-text-muted)] max-w-xs mt-4 leading-relaxed opacity-70">
              {t('chat.trustHint')}
            </p>
          </div>
        ) : (
          <div className="px-4 py-4 max-w-2xl mx-auto w-full">
            {messages.map((msg, i) => (
              <MessageBubble key={i} message={msg} onShowDetail={onShowDetail} token={token} onSendMessage={onSend} />
            ))}
            {sending && (
              <div className="flex justify-start mb-3">
                <div className="bg-[var(--aif-bg-surface)] rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-[var(--aif-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[var(--aif-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[var(--aif-text-muted)] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Quick reply chips above input bar */}
      {activeChips.length > 0 && !sending && (
        <div className="flex-shrink-0 px-4 pt-2 bg-[var(--aif-bg-page)]">
          <div className="max-w-2xl mx-auto overflow-x-auto scrollbar-hide">
            <div className="flex gap-1.5 pb-1">
              {activeChips.map((chip, i) => (
                <button
                  key={i}
                  onClick={() => handleQuickReply(chip.value)}
                  className="flex-shrink-0 px-3 py-1.5 text-xs rounded-full border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] text-[var(--aif-text-secondary)] hover:border-[var(--aif-color-primary)] hover:text-[var(--aif-color-primary)] transition-all active:scale-95 whitespace-nowrap"
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* File preview bar */}
      {pendingFile && (
        <div className="flex-shrink-0 px-4 pt-2 bg-[var(--aif-bg-page)]">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 px-3 py-2 bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl">
              {pendingFile.type.startsWith('image/') ? (
                <img
                  src={URL.createObjectURL(pendingFile)}
                  alt="Preview"
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--aif-text-primary)] truncate">{pendingFile.name}</p>
                <p className="text-xs text-[var(--aif-text-muted)]">{(pendingFile.size / 1024).toFixed(0)} KB</p>
              </div>
              <button
                onClick={clearPendingFile}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[var(--aif-bg-elevated)] transition text-[var(--aif-text-muted)]"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input bar */}
      <div className="flex-shrink-0 px-4 pb-2 pt-2 bg-[var(--aif-bg-page)]">
        <div className="flex items-end gap-2 max-w-2xl mx-auto">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            className="w-11 h-11 flex items-center justify-center rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)] hover:text-[var(--aif-color-primary)] hover:border-[var(--aif-color-primary)] disabled:opacity-30 flex-shrink-0"
            title={t('upload.attach')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
            </svg>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            capture="environment"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              data-chat-input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={pendingFile ? t('upload.addMessage') : t('chat.placeholder')}
              rows={1}
              className="w-full resize-none rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] px-4 py-3 pr-12 text-sm focus:outline-none focus:border-[var(--aif-border-focus)] transition-colors text-[var(--aif-text-primary)] placeholder:text-[var(--aif-text-muted)]"
              style={{ maxHeight: '120px' }}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
            />
            {hasSpeech && !pendingFile && (
              <button
                onClick={toggleVoice}
                className={`absolute right-2 bottom-2 w-8 h-8 flex items-center justify-center rounded-lg transition ${
                  listening
                    ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 animate-pulse'
                    : 'text-[var(--aif-text-muted)] hover:text-[var(--aif-color-primary)] hover:bg-[var(--aif-bg-elevated)]'
                }`}
                title={listening ? t('chat.voiceListening') : t('chat.voice')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-14 0m14 0a7 7 0 00-14 0m14 0v1a7 7 0 01-14 0v-1m7 8v3m-4 0h8M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3z" />
                </svg>
              </button>
            )}
          </div>
          <button
            onClick={handleSend}
            disabled={(!input.trim() && !pendingFile) || sending}
            className="w-11 h-11 flex items-center justify-center bg-[var(--aif-color-primary)] text-white rounded-2xl transition disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0 hover:opacity-90 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
